import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, UserCheck, X, AlertCircle, Users, ChevronDown } from 'lucide-react';
import { ROLE_LABELS } from '../../lib/constants';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui/Dropdown';

const ENTITY_CONFIG = {
  PROFILE: { label: 'Profile', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  PREQUALIFICATION: { label: 'Prequalification', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  TENDER: { label: 'Tender / Proposal', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
};

export default function ApproveRequestModal({ request, users = [], onConfirm, onClose }) {
  const requester = request?.requestedBy;
  const entityType = request?.entityType || 'PROFILE';
  const entityConfig = ENTITY_CONFIG[entityType] || { label: entityType, badge: 'bg-gray-100 text-gray-700 border-gray-200' };

  const [assignMode, setAssignMode] = useState('requester'); // 'requester' | 'other'
  const [selectedUserId, setSelectedUserId] = useState(requester?.id || '');
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const assignableUsers = (users || []).filter((u) => u.is_active !== false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const targetAssigneeId =
      assignMode === 'requester' ? requester?.id || selectedUserId : selectedUserId;

    try {
      setIsSubmitting(true);
      await onConfirm({
        assignedSalespersonId: targetAssigneeId ? parseInt(targetAssigneeId) : undefined,
        remarks: remarks.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to approve request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${entityConfig.badge}`}>
                  {entityConfig.label}
                </span>
                <h3 className="text-base font-bold text-gray-900">Approve Request</h3>
              </div>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                {request?.title || 'Approval Request'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Error */}
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submitter Info */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/70 flex items-center justify-between text-xs">
            <div>
              <span className="text-gray-400 font-medium block mb-0.5">Submitted by</span>
              <span className="font-semibold text-gray-800 text-sm">
                {requester?.username || 'Team Member'}
              </span>
              {requester?.role && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-gray-600 text-[10px] font-medium">
                  {ROLE_LABELS[requester.role] || requester.role}
                </span>
              )}
            </div>
            <div className="text-right">
              <span className="text-gray-400 font-medium block mb-0.5">Submitted on</span>
              <span className="font-medium text-gray-700">
                {request?.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'Today'}
              </span>
            </div>
          </div>

          {/* Assignee Selection */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5 text-blue-600" />
              Assign Lead Owner / Salesperson
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                  assignMode === 'requester'
                    ? 'border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="assignMode"
                  value="requester"
                  checked={assignMode === 'requester'}
                  onChange={() => {
                    setAssignMode('requester');
                    if (requester?.id) setSelectedUserId(requester.id);
                  }}
                  className="mt-0.5 text-emerald-600 focus:ring-emerald-500 accent-emerald-600"
                />
                <div className="text-xs">
                  <div className="font-semibold text-gray-800">Assign to Submitter</div>
                  <div className="text-gray-400 text-[11px] truncate">{requester?.username || 'Submitter'}</div>
                </div>
              </label>
              <label
                className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                  assignMode === 'other'
                    ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-400'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="assignMode"
                  value="other"
                  checked={assignMode === 'other'}
                  onChange={() => setAssignMode('other')}
                  className="mt-0.5 text-blue-600 focus:ring-blue-500 accent-blue-600"
                />
                <div className="text-xs">
                  <div className="font-semibold text-gray-800">Assign to Someone Else</div>
                  <div className="text-gray-400 text-[11px]">Select team member</div>
                </div>
              </label>
            </div>

            {assignMode === 'other' && (
              <div className="animate-fade-in">
                <Dropdown className="w-full">
                  <DropdownTrigger>
                    <div className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-white border rounded-xl text-xs shadow-sm cursor-pointer transition-all ${
                      selectedUserId ? 'border-blue-400 ring-1 ring-blue-400 text-gray-800' : 'border-gray-200 text-gray-400 hover:border-gray-400'
                    }`}>
                      <span className="truncate">
                        {selectedUserId
                          ? (() => { const u = assignableUsers.find(x => String(x.id) === String(selectedUserId)); return u ? `${u.username} (${ROLE_LABELS[u.role] || u.role})` : '— Select Team Member —'; })()
                          : '— Select Team Member —'
                        }
                      </span>
                      <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
                    </div>
                  </DropdownTrigger>
                  <DropdownPopover>
                    <DropdownMenu
                      selectionMode="single"
                      selectedKeys={selectedUserId ? new Set([String(selectedUserId)]) : new Set()}
                      onSelectionChange={(keys) => setSelectedUserId([...keys][0] || '')}
                    >
                      {assignableUsers.map((u) => (
                        <DropdownItem key={u.id} id={String(u.id)}>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-800">{u.username}</span>
                            <span className="text-[11px] text-gray-400">{ROLE_LABELS[u.role] || u.role}</span>
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
            )}
          </div>

          {/* Remarks */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700">
              Approval Remarks <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any guidance or instructions for the assignee..."
              rows={3}
              maxLength={500}
              className="w-full text-xs rounded-xl border border-gray-200 p-3 text-gray-800 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
            />
          </div>

          {/* Footer Actions */}
          <div className="pt-1 flex items-center justify-end gap-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Approve & Assign
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
